import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Chip, Spinner, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar } from '@heroui/react';
import { Icon } from '@iconify/react';
import { UniversalTable, IdView, Typography } from '../../../../components';
import { EmptyContentIc } from '../../../../components/icons';
import RevshareDetailsCell from './RevshareDetailsCell';
import StatisticsCell from './StatisticsCell';
import { useTranslations } from '../../../../contexts/translation.context';
import { PaginationInfo, TableColumn } from '../../../../types/account';
import {
  RefShareOffer,
  RefShareOfferOwner,
  OfferStatus,
  OwnerLevel,
} from '../../../../api/services';

type RevshareOffersTableProps = {
  offers: RefShareOffer[];
  pagination: PaginationInfo;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onStatusChange?: (modelId: string, status: OfferStatus) => void;
};

const statusConfig: Record<OfferStatus, { bgColor: string; textColor: string; label: string }> = {
  active: { bgColor: 'rgba(23, 201, 100, 0.15)', textColor: '#17c964', label: 'Активный' },
  hidden: { bgColor: '#F4F4F5', textColor: '#71717A', label: 'Неактивный' },
  moderation: { bgColor: 'rgba(232, 163, 35, 0.15)', textColor: '#E8A323', label: 'На модерации' },
  refusal: { bgColor: 'rgba(239, 68, 68, 0.15)', textColor: '#ef4444', label: 'Отменен' },
};

const levelConfig: Record<OwnerLevel, { bgColor: string; textColor: string; label: string }> = {
  // Partner levels
  new: { bgColor: 'rgba(23, 201, 100, 0.15)', textColor: '#17C964', label: 'New' },
  base: { bgColor: '#EEEEEF', textColor: '#71717A', label: 'Base' },
  pro: { bgColor: '#11181c', textColor: '#ffffff', label: 'Pro' },
  expert: { bgColor: 'rgba(120, 40, 200, 0.15)', textColor: '#7828c8', label: 'Expert' },
  // Creator levels
  bronze: { bgColor: '#FDF4E7', textColor: '#CD7F32', label: 'Bronze' },
  silver: { bgColor: '#F0F0F0', textColor: '#808080', label: 'Silver' },
  gold: { bgColor: '#FFF9E6', textColor: '#FFD700', label: 'Gold' },
  platinum: { bgColor: '#E8F4F8', textColor: '#A0B2C6', label: 'Platinum' },
  diamond: { bgColor: '#E6F3FF', textColor: '#00BFFF', label: 'Diamond' },
};

const columns: TableColumn[] = [
  { key: 'id', name: 'ID оффера' },
  { key: 'owner', name: 'Владелец' },
  { key: 'model', name: 'Модель' },
  { key: 'offerDetails', name: 'Детали RevShare' },
  { key: 'statistics', name: 'Статистика' },
  { key: 'status', name: 'Статус' },
  { key: 'actions', name: 'Действия' },
];

const getOwnerData = (owner: RefShareOfferOwner | string | undefined): RefShareOfferOwner | null => {
  if (!owner) return null;
  if (typeof owner === 'string') return null;
  return owner;
};

export const RevshareOffersTable: React.FC<RevshareOffersTableProps> = ({
  offers,
  pagination,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onStatusChange,
}) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const renderCell = useCallback((offer: RefShareOffer, columnKey: string) => {
    const offerId = offer._id || offer.id || '';
    const ownerData = getOwnerData(offer.owner);
    const offerStatus = offer.offer?.status || 'moderation';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={offerId}>
            {offerId.slice(-4)}
          </IdView>
        );

      case 'owner': {
        if (!ownerData) {
          return <span className="text-[#71717A]">—</span>;
        }

        const ownerId = ownerData._id || ownerData.id || '';
        const ownerType = ownerData.type || 'creator';
        const href = `/users/creators/${ownerId}`;

        const level = ownerData.lvl;
        const levelStyle = level ? levelConfig[level] : null;

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={ownerData.avatar}
              showFallback
              name={ownerData.name?.charAt(0)}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200 uppercase text-[14px] font-medium"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[4px]">
              <span
                onClick={() => navigate(href)}
                className="text-[#006FEE] text-[14px] font-semibold hover:underline cursor-pointer"
              >
                {ownerData.name}
              </span>
              <div className="flex items-center gap-[6px]">
                {levelStyle && (
                  <span
                    className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
                    style={{
                      backgroundColor: levelStyle.bgColor,
                      color: levelStyle.textColor,
                    }}
                  >
                    {levelStyle.label}
                  </span>
                )}
                {/* Type Badge - Creator/Agency */}
                <span
                  className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
                  style={{
                    backgroundColor: ownerType === 'agency' ? 'rgba(120, 40, 200, 0.2)' : 'rgba(0, 111, 238, 0.2)',
                    color: ownerType === 'agency' ? '#7828C8' : '#006FEE'
                  }}
                >
                  {ownerType === 'agency' ? t('admin.creators.type.agency') : t('admin.creators.type.creator')}
                </span>
              </div>
            </div>
          </div>
        );
      }

      case 'model': {
        const modelName = offer.name || offer.of_user_name || '';
        const modelUsername = offer.of_user_name || '';
        const popularityPercent = offer.of_data?.overall_popularity_percent || 0;
        const onlyfansLink = modelUsername ? `https://onlyfans.com/${modelUsername}` : '';

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={offer.avatar}
              showFallback
              name={modelName?.charAt(0)}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[2px]">
              <div className="flex items-center gap-[8px]">
                <Typography variant="body2" className="font-semibold text-[#11181C]">
                  {modelName}
                </Typography>
                {modelUsername && (
                  <a
                    href={onlyfansLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#006FEE] text-[14px] font-semibold hover:underline"
                  >
                    @{modelUsername}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-[6px]">
                <Chip
                  classNames={{
                    base: 'h-[18px] px-[6px] bg-[#F5A524]/20',
                    content: 'text-[#F5A524] text-[11px] font-medium'
                  }}
                >
                  Top {popularityPercent.toFixed(1)}%
                </Chip>
              </div>
            </div>
          </div>
        );
      }

      case 'offerDetails':
        return <RevshareDetailsCell offer={offer} />;

      case 'statistics':
        return <StatisticsCell offer={offer} />;

      case 'status': {
        const cfg = statusConfig[offerStatus] || statusConfig.moderation;
        return (
          <Chip
            className="h-[22px] px-[8px] text-[12px] font-medium"
            style={{
              backgroundColor: cfg.bgColor,
              color: cfg.textColor,
            }}
          >
            {cfg.label}
          </Chip>
        );
      }

      case 'actions': {
        const canActivate = offerStatus !== 'active' && offerStatus !== 'refusal';
        const canDeactivate = offerStatus === 'active';

        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => {
                if (key === 'activate' && canActivate) {
                  onStatusChange?.(offerId, 'active');
                } else if (key === 'deactivate' && canDeactivate) {
                  onStatusChange?.(offerId, 'hidden');
                }
              }}
              disabledKeys={[
                ...(canActivate ? [] : ['activate']),
                ...(canDeactivate ? [] : ['deactivate']),
              ]}
            >
              <DropdownItem key="activate" className={canActivate ? '' : 'opacity-50'}>
                <div className="flex items-center gap-[8px]">
                  <Icon icon="solar:check-circle-linear" fontSize={16} className="text-[#17c964]" />
                  <span>Активировать</span>
                </div>
              </DropdownItem>
              <DropdownItem key="deactivate" className={canDeactivate ? '' : 'opacity-50'}>
                <div className="flex items-center gap-[8px]">
                  <Icon icon="solar:close-circle-linear" fontSize={16} className="text-[#ef4444]" />
                  <span>Деактивировать</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );
      }

      default:
        return null;
    }
  }, [onStatusChange, navigate, t]);

  const emptyContent = isLoading ? (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <Spinner color="primary" />
      <Typography variant="body2" className="text-[#71717A]">
        Загрузка...
      </Typography>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <EmptyContentIc />
      <Typography variant="body1" className="font-semibold">
        Офферы не найдены
      </Typography>
      <Typography variant="body3" className="text-[#71717A]">
        Попробуйте изменить параметры поиска
      </Typography>
    </div>
  );

  return (
    <UniversalTable
      data={offers}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={pagination}
      currentPage={pagination.page}
      currentPageSize={pagination.page_size}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isSearchEmpty={!offers.length}
    />
  );
};
